import { MetaOsHubHostName, MetaOsHubName, getMetaOsHubContextThrows } from '@ms/yammer-web-support/dist/metaOsHub';
import { isMobileEmbedView } from '@ms/yammer-web-support/dist/userAgent';
import { getVivaConnectionsContext } from 'mobileEmbedView';
import { NonOptionalTeamsContext, TeamsAppType, TeamsContext, TeamsThemeType } from 'state/teams';
export { MetaOsHubNotFoundError } from '@ms/yammer-web-support/dist/metaOsHub';

export const VivaEngageAppEntityId = 'vivaengage';

const supportedThemes: TeamsThemeType[] = ['default', 'dark', 'contrast'];
const isThemeSupported = (theme: string): theme is TeamsThemeType => (supportedThemes as string[]).includes(theme);

type GetSupportedTheme = (theme: string | undefined) => TeamsThemeType;
const getSupportedTheme: GetSupportedTheme = (theme) => (theme && isThemeSupported(theme) ? theme : 'default');

type GetTeamsAppType = (teamId: string | undefined) => TeamsAppType;
const getTeamsAppType: GetTeamsAppType = (teamId) => (teamId ? 'tab' : 'personal');

const hostNameToHubNameMapping: Readonly<Record<MetaOsHubHostName, MetaOsHubName | undefined>> = {
  [MetaOsHubHostName.office]: MetaOsHubName.Office,
  [MetaOsHubHostName.orange]: undefined,
  [MetaOsHubHostName.outlook]: MetaOsHubName.Outlook,
  [MetaOsHubHostName.outlookWin32]: MetaOsHubName.Outlook,
  [MetaOsHubHostName.places]: undefined,
  [MetaOsHubHostName.teams]: MetaOsHubName.Teams,
  [MetaOsHubHostName.teamsModern]: MetaOsHubName.Teams,
};

type GetContextFromVivaConnections = () => NonOptionalTeamsContext;
const getContextFromVivaConnections: GetContextFromVivaConnections = () => {
  const context = getVivaConnectionsContext();

  if (context == null) {
    throw new Error('Unable to get context: not loaded inside Viva Connections app.');
  }

  return {
    appSessionId: context.appSessionId,
    appType: 'personal',
    channelId: undefined,
    deepLinkPath: undefined,
    entityId: undefined,
    groupId: undefined,
    hostClientType: context.hostClientType,
    hostAppName: undefined,
    hubName: undefined,
    locale: context.locale,
    ringId: context.ringId,
    sessionId: context.sessionId,
    subEntityId: undefined,
    teamId: undefined,
    theme: getSupportedTheme(context.theme),
    userClickTime: undefined,
    appLaunchId: undefined,
  };
};

type GetContextFromMetaOsHub = () => Promise<NonOptionalTeamsContext>;
const getContextFromMetaOsHub: GetContextFromMetaOsHub = async () => {
  const { app, channel, page, team } = await getMetaOsHubContextThrows();
  const teamId = team?.internalId;

  return {
    appSessionId: app.sessionId,
    appLaunchId: app.appLaunchId,
    appType: getTeamsAppType(teamId),
    channelId: channel?.id,
    deepLinkPath: undefined,
    entityId: page.id,
    groupId: team?.groupId,
    hostClientType: app.host.clientType,
    hostAppName: app.host.name,
    hubName: hostNameToHubNameMapping[app.host.name],
    locale: app.locale,
    ringId: app.host.ringId,
    sessionId: app.host.sessionId,
    subEntityId: page.subPageId,
    teamId,
    theme: getSupportedTheme(app.theme),
    userClickTime: app.userClickTime,
  };
};

type GetContext = () => Promise<TeamsContext>;
export const getContext: GetContext = async () =>
  isMobileEmbedView() ? getContextFromVivaConnections() : getContextFromMetaOsHub();
